import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { analytics } from '@src/utility/analytics';
import {
  Bold,
  BoldValues,
  findClosestBoldValueFont,
  getBoldValuesFromCustomFonts,
  getCustomFontFromPostScriptName,
} from '@src/utility/fonts';
import { observer } from 'mobx-react-lite';
import { forwardRef, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import CircleCheckIcon from '../../../svgs/CircleCheckIcon';
import { KaraokeCard } from '../../../types.ts/general';
import { Breadcrumbs } from '../../Breadcrumbs';
import SpinningLoading from '../../SpinningLoading';
import GlobalKaraokePresetSelect from '../GlobalKaraokePresetSelect';
import { SettingsPreviewSection } from '../SettingsPreviewSection';
import { CaptionsPreviewThumbnail } from '../VideoTemplateSettings/CaptionsPreviewThumbnail';
import KaraokeSaveOrCancelTemplate from './KaraokeSaveOrCancelTemplate';
import KaraokeTemplateSelect from './KaraokeTemplateSelect';
import {
  Action,
  AlignValues,
  Readability,
  TextSettingsUI,
} from './TextSettingsUI';

import type { Align } from './TextSettingsUI';
import type { ElementState } from '@src/renderer/ElementState';
import type { KaraokeConfig } from '@src/services/karaoke/karaokeTypes';
import type { EditorStoreLike } from '@src/utility/compositionSettings';

type Props = {
  activeElement: ElementState | undefined;
  editorStore: EditorStoreLike;
};

const AIKaraokeTextProducer = observer(
  forwardRef<HTMLDivElement, Props>(({ activeElement, editorStore }, ref) => {
    const videoCreator = useVideoCreatorStore();
    // Use computed getter so component reacts to karaoke generation
    const isKaraokeGenerated = videoCreator.isKaraokeGenerated;

    const config = videoCreator.karaokeProducer.getKaraokeConfig();

    const [addNewTemplate, toggleAddNewTemplate] = useState<boolean>(false);
    const [templateName, setTemplateName] = useState<string>();
    const [templateNameError, setTemplateNameError] = useState<boolean>(false);
    const [allowCustomTemplateSave, setAllowCustomTemplateSave] =
      useState<boolean>(false);

    const selectedShadow = useMemo(
      () =>
        config.shadow_color
          ? {
              color: config.shadow_color as string,
              blur: config.shadow_blur as number,
              x: config.shadow_x as number,
              y: config.shadow_y as number,
            }
          : undefined,
      [config],
    );

    const hasStickyBottom =
      addNewTemplate ||
      allowCustomTemplateSave ||
      videoCreator.textBrandingService.isTemplateModified;

    function updateSelectedActionsForReadability(
      newConfig: any,
      selectedActions: (Action | Readability)[],
    ) {
      for (const action in Readability) {
        if (Object.prototype.hasOwnProperty.call(Readability, action)) {
          const readabilityAction =
            Readability[action as keyof typeof Readability];

          if (
            newConfig[`hide${readabilityAction}` as keyof typeof newConfig] &&
            !selectedActions.includes(readabilityAction)
          ) {
            selectedActions.push(readabilityAction);
          } else if (
            !newConfig[`hide${readabilityAction}` as keyof typeof newConfig] &&
            selectedActions.includes(readabilityAction)
          ) {
            selectedActions = selectedActions.filter(
              action => action !== readabilityAction,
            );
          }
        }
      }
    }

    useEffect(() => {
      const selectedActions: (Action | Readability)[] = [];
      if (config.font_style === 'italic') {
        selectedActions.push(Action.italic);
      }
      if (config.text_transform === 'uppercase') {
        selectedActions.push(Action.capitalize);
      }
      updateSelectedActionsForReadability(config, selectedActions);
      setSelectedActions(selectedActions);
    }, [config]);

    useEffect(() => {
      return () => {
        if (videoCreator.textBrandingService.isTemplateModified) {
          videoCreator.textBrandingService.isTemplateModified = false;
        }
      };
    }, []);

    const [selectedActions, setSelectedActions] = useState<
      (Action | Readability)[]
    >([Action.align]);

    const brandCustomFonts =
      videoCreator.organization?.branding?.customFonts || [];
    const fontFamily = config.font_family;
    const fontWeight = config.font_weight;
    const selectedBold = useMemo(() => {
      const customFont = getCustomFontFromPostScriptName(
        fontFamily,
        brandCustomFonts,
      );
      if (customFont) return customFont.fontSubfamily;
      return Object.keys(BoldValues).find(
        (k: any) => BoldValues[k as Bold] === Number(fontWeight),
      ) as Bold;
    }, [brandCustomFonts, fontFamily, fontWeight]);

    const toggleAction = (action: Action) => {
      const hasAction = selectedActions.find(a => a === action);
      let updatedActions = [...selectedActions];
      if (hasAction) {
        updatedActions = updatedActions.filter(a => a !== action);
      } else {
        updatedActions = [...updatedActions, action];
      }
      setSelectedActions(updatedActions);
    };

    const modifyProperty = async (propertyMap: Partial<KaraokeConfig>) => {
      const newConfig = { ...config, ...propertyMap };

      videoCreator.textBrandingService.checkPropsChanged(
        'karaoke',
        propertyMap,
      );
      videoCreator.karaokeProducer.rerenderWithNewConfig(newConfig);
    };

    const setAnimation = (
      time: string | number | null,
      type: any,
      duration: number | null = null,
      background_effect: string | null = null,
    ) => {
      const isInstagram = type === 'instagram';
      // Remove existing animation from list
      const newAnimations =
        config.animations?.filter((keyframe: any) =>
          [0, 'start'].includes(time!)
            ? ![0, 'start'].includes(keyframe.time)
            : keyframe.time !== time,
        ) ?? [];

      if (type !== 'none') {
        if (isInstagram) {
          type = 'text-appear';
        }
        const animation: any = { type };

        if (time) {
          animation.time = time;
          animation.duration = duration || 1;
        }

        if (background_effect) {
          animation.background_effect = background_effect;
        }

        // Reverse animation when used as exit animation
        if (time === 'end') {
          animation.reversed = true;
        }

        newAnimations.push(animation);
      }
      const newConfig: KaraokeConfig = {
        ...config,
        instagramEffect: isInstagram,
        ...(!config.instagramEffect &&
          isInstagram && {
            hideComma: true,
            hidePeriod: true,
          }),
        language: isInstagram ? 'original' : config.language,
        animations: newAnimations,
      };
      videoCreator.textBrandingService.checkPropsChanged('karaoke', {
        animations: newAnimations,
      });
      videoCreator.karaokeProducer.rerenderWithNewConfig(newConfig);
    };

    const handleToggleAction = (
      action: Action,
      propertyName: keyof KaraokeConfig,
      normalState: string,
      requiredState: string,
    ) => {
      // debugger;
      toggleAction(action);
      const isSelected = selectedActions.includes(action);
      const state = isSelected ? normalState : requiredState;
      modifyProperty({ [propertyName]: state });
    };

    const handleSanitizePunctuation = async (action: Readability) => {
      const newConfig = { ...config };
      if (action === Readability.comma) {
        newConfig.hideComma = !config.hideComma;
      }
      if (action === Readability.period) {
        newConfig.hidePeriod = !config.hidePeriod;
      }
      if (action === Readability.filler) {
        newConfig.hideFillers = !config.hideFillers;
        newConfig.temp_muteFillers = true;
      }
      await produceKaraokeText(newConfig);
    };

    const toggleAlign = (align: Align) => {
      const modifications = {
        x_alignment: AlignValues[align],
        x: '50%', // default position to keep it visible
      };
      modifyProperty(modifications);
    };

    const handleSetBold = (
      bold: string,
      fontWeight: number,
      fontScriptName?: string,
    ) => {
      const state = selectedBold === bold ? null : bold;
      const value = state ? fontWeight : BoldValues[Bold.normal];
      modifyProperty({
        font_weight: value.toString(),
        ...(fontScriptName && { font_family: fontScriptName }),
      });
    };

    const handleSetFont = (
      fontScriptName?: string,
      variant?: { weight: number; style?: 'normal' | 'italic' },
    ) => {
      // Explicit brand-variant pick from the font dropdown — apply the chosen
      // variant's own weight + style verbatim. Skips the closest-bold
      // resolution below, which would override the user's specific choice and
      // drop the italic style (so italic variants never applied).
      if (fontScriptName && variant) {
        modifyProperty({
          font_family: fontScriptName,
          font_weight: variant.weight.toString(),
          // Popular-font weight picks carry no style — the italic
          // toggle owns font_style there; brand variants specify
          // their face's own.
          ...(variant.style ? { font_style: variant.style } : {}),
        });
        return;
      }
      let _fontWeight = config.font_weight;
      if (fontScriptName) {
        const isCustomFont = !!getCustomFontFromPostScriptName(
          fontScriptName,
          brandCustomFonts,
        );
        if (isCustomFont) {
          const { fontsByWeight } = getBoldValuesFromCustomFonts(
            fontScriptName,
            brandCustomFonts,
          );
          const closestBold = findClosestBoldValueFont(fontsByWeight);
          if (closestBold) {
            _fontWeight = closestBold.weight.toString();
            fontScriptName = closestBold.font.postScriptName;
          }
        } else {
          _fontWeight = BoldValues[Bold.normal].toString();
        }
      }
      modifyProperty({ font_family: fontScriptName, font_weight: _fontWeight });
    };

    const produceKaraokeText = async (
      newConfig = {
        ...config,
        ...(!isKaraokeGenerated &&
          videoCreator.karaokeProducer.getKaraokeTextSettingByAspectRatio()),
      },
    ) => {
      analytics.track('karaoke_text_generated', {
        hideComma: newConfig.hideComma,
        hidePeriod: newConfig.hidePeriod,
        hideFillers: newConfig.hideFillers,
        font_family: newConfig.font_family,
        font_size: newConfig.font_size,
        fill_color: newConfig.fill_color,
        background_color: newConfig.background_color,
        shadow_color: newConfig.shadow_color,
        shadow_blur: newConfig.shadow_blur,
        shadow_x: newConfig.shadow_x,
        shadow_y: newConfig.shadow_y,
        font_weight: newConfig.font_weight,
        x_alignment: newConfig.x_alignment,
        text_transform: newConfig.text_transform,
        font_style: newConfig.font_style,
        animations: newConfig.animations,
        storyId: videoCreator.story?.id,
        storyTitle: videoCreator.story?.title,
        videoId: videoCreator.currentVideo?.id,
        videoTitle: videoCreator.currentVideo?.title,
      });

      videoCreator.karaokeLoading = true;
      try {
        await videoCreator.karaokeProducer!.produceKaraoke(newConfig);
        videoCreator.setShowSubtitles(true);

        // Save karaokeTemplateId to extraElementData when producing karaoke
        const templateId =
          videoCreator.textBrandingService.selectedTemplateId || 'default';
        videoCreator.textBrandingService.saveToExtraElementData(
          'karaoke',
          templateId,
        );
        if (
          !isKaraokeGenerated &&
          !videoCreator.textBrandingService.selectedTemplateId
        ) {
          videoCreator.textBrandingService.selectedTemplateId = 'default';
        }
      } catch (e) {
        console.error('Error generating karaoke text:', e);
      } finally {
        videoCreator.karaokeLoading = false;
      }
    };

    const karaokeElement = config;
    const alignType = Object.keys(AlignValues).find(
      k => AlignValues[k as Align] === karaokeElement.x_alignment,
    ) as Align;

    // Templates list + add handler (mirrors logic in TextBrandingTemplates when using renderFunction)
    const handleAddTemplateButtonClick = async () => {
      if (!isKaraokeGenerated) {
        await produceKaraokeText();
      }
      videoCreator.textBrandingService.isTemplateModified = false;
      videoCreator.textBrandingService.prepareTemplateToSave = true;
      videoCreator.karaokeSubMenu = KaraokeCard.styles;
      toggleAddNewTemplate(true);
      setAllowCustomTemplateSave(true);
    };

    const aspectRatio = videoCreator.getCurrentAspectRatio();

    // Ensure karaoke templates are loaded when navigating to the Templates tab.
    // In this panel we are not using <TextBrandingTemplates /> (which normally triggers the load),
    // so we eagerly fetch them when the submenu switches to templates or if still empty.
    // FIXME: this is unnecessary complexity
    useEffect(() => {
      const service = videoCreator.textBrandingService;
      if (
        !service.isTemplateTypeLoaded('karaoke', aspectRatio) &&
        !service.isLoading
      ) {
        // hasActiveElement indicates whether we should attempt to derive template id from element
        const hasActiveElement = !!activeElement;
        service.getAllBrandingTemplates(
          'karaoke',
          aspectRatio,
          hasActiveElement,
          true, // we don't want to apply some template here, just load
        );
      }
    }, [aspectRatio, activeElement, videoCreator.textBrandingService]);

    const templates = (
      videoCreator.textBrandingService.brandingTemplates || []
    ).filter(t => {
      const templateId = typeof t.id === 'string' ? t.id : undefined;
      return !videoCreator.textBrandingService.isPresetTemplate(templateId);
    });

    const karaokeLabel = videoCreator.textBrandingService.prepareTemplateToSave
      ? 'Custom'
      : videoCreator.textBrandingService.selectedBrandingTemplate?.title
        ? videoCreator.textBrandingService.selectedBrandingTemplate.title.toString()
        : 'Custom';

    const renderStylesTabContent = () =>
      isKaraokeGenerated && (
        <div style={{ gap: '10px', display: 'flex', flexDirection: 'column' }}>
          <Breadcrumbs
            items={[
              {
                label: 'Captions',
                onClick: () =>
                  (videoCreator.karaokeSubMenu = KaraokeCard.templates),
              },
            ]}
          />
          <SettingsPreviewSection label={karaokeLabel}>
            <CaptionsPreviewThumbnail videoCreator={videoCreator} />
          </SettingsPreviewSection>

          <TextSettingsUI
            ref={ref}
            skipFields={['text']}
            karaokeElement={karaokeElement}
            selectedBold={selectedBold}
            selectedFontWeight={fontWeight}
            selectedActions={selectedActions}
            selectedFontFamily={fontFamily}
            selectedShadow={selectedShadow}
            modifyProperty={modifyProperty}
            handleSetFont={handleSetFont}
            handleSetBold={handleSetBold}
            handleToggleAction={handleToggleAction}
            handleSanitizePunctuation={handleSanitizePunctuation}
            hasStickyBottom={hasStickyBottom}
            alignType={alignType}
            toggleAlign={toggleAlign}
            setAnimation={setAnimation}
            animations={
              config.animations?.reduce<{
                enter?: string;
                exit?: string;
              }>((acc, animation) => {
                const { time, type } = animation as {
                  time?: 'start' | 'end' | number;
                  type?: string;
                };
                if (!time || !type) {
                  return acc;
                }
                if (time === 'start' || time === 0) {
                  acc.enter = type;
                } else if (time === 'end') {
                  acc.exit = type;
                }
                return acc;
              }, {}) || {}
            }
            editorStore={editorStore}
            activeElement={undefined} // on purpose
          />
        </div>
      );

    const renderTemplatesTabContent = () => (
      <>
        <KaraokeTemplateSelect
          // fallback to allow settings
          config={{
            ...config,
            parent_template_id: config.parent_template_id || 'settings',
          }}
          onAddTemplateButtonClick={handleAddTemplateButtonClick}
          templates={templates}
          produceKaraokeText={produceKaraokeText}
        />
        <GlobalKaraokePresetSelect
          produceKaraokeText={produceKaraokeText}
          config={config}
        />
      </>
    );

    const renderCurrentTabContent = () => {
      // When karaoke is not generated, always show templates regardless of submenu
      if (!isKaraokeGenerated) {
        return renderTemplatesTabContent();
      }
      switch (videoCreator.karaokeSubMenu) {
        case KaraokeCard.templates:
          return renderTemplatesTabContent();
        default:
          return renderStylesTabContent();
      }
    };

    return (
      <Main>
        {videoCreator.renderer?.ready && (
          <>
            {renderCurrentTabContent()}
            {isKaraokeGenerated &&
              videoCreator.karaokeSubMenu !== KaraokeCard.templates && (
                <KaraokeSaveOrCancelTemplate
                  config={config}
                  templates={templates}
                  addNewTemplate={addNewTemplate}
                  setTemplateName={setTemplateName}
                  setTemplateNameError={setTemplateNameError}
                  templateName={templateName}
                  templateNameError={templateNameError}
                  toggleAddNewTemplate={toggleAddNewTemplate}
                  allowCustomTemplateSave={allowCustomTemplateSave}
                  setAllowCustomTemplateSave={setAllowCustomTemplateSave}
                  type="karaoke"
                  setting="karaoke-settings"
                />
              )}
            {/* Show Generate button only on styles tab when no karaoke exists (edge case) */}
            {videoCreator.karaokeSubMenu !== KaraokeCard.templates &&
              (!videoCreator.rootStore.karaokeStore.hasKaraokeLines ||
                !isKaraokeGenerated) && (
                <GenerateButton onClick={() => produceKaraokeText()}>
                  <CircleCheckIcon strokeColor={'black'} />
                  Generate Karaoke Text
                </GenerateButton>
              )}
          </>
        )}

        {videoCreator.karaokeLoading &&
          !videoCreator.textBrandingService.isPropsChanged && (
            <SpinningLoading
              text={
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span>Generating karaoke text…</span>
                </div>
              }
            />
          )}
        {videoCreator.isTimelineLoading && (
          <SpinningLoading
            text={
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <span>Updating timeline…</span>
              </div>
            }
          />
        )}
      </Main>
    );
  }),
);

export default AIKaraokeTextProducer;

const Main = styled.div`
  padding-top: 16px;
`;

const GenerateButton = styled.div`
  width: 100%;
  display: flex;
  height: 48px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  border-radius: var(--radius);
  background: var(--primary);
  box-sizing: border-box;

  color: var(--background);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-bold);
  cursor: pointer;
  margin-top: 10px;
`;
